import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IGetValueAsPromiseRequestPayload } from '../models/models';
import { CategoryAPIName, eSearchType, IAzureIndexResponseModel, IClientSideSearchInfo, IGetNavLinkResponseModel, IGetQuickSearchRequestPayload, IQuickNavInfoResponse, IQuickSearchResponseItem, QuickSearchCategory, QuickSearchResultModel } from '../../core/models/quick-nav.model';
import { AuthService, Default_SessionUserDetails, SessionData_Response } from '../../core/services/auth.service';
import axios from 'axios';

@Injectable({
  providedIn: 'root'
})
export class QuickNavApiService {

  //#region Private State
  private currentUser: SessionData_Response = Default_SessionUserDetails; //Stores the currently logged-in user session details.
  private basePath: string = '';
  private quickNavInfoResponse!: IQuickNavInfoResponse;
  private lastFetchedDate!: Date;

  private MAX_AGE_MILLISECONDS: number = 5 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private auth: AuthService
  ) {
    this.basePath = `${environment.api.url}/WebServices/QuickNav.asmx`;
    // Subscribe to session updates to track the current user    
  }

  public getSearchInfo(): Observable<IQuickNavInfoResponse> {
    return this.auth.currentSession$.pipe(
      take(1),
      //tap((res) => console.log(`Got session response: ${JSON.stringify(res)}`)),
      switchMap((sessionResponse) => {
        return this.http.post<IQuickNavInfoResponse>(`${this.basePath}/GetSearchInfo`, { userId: sessionResponse?.UserId }).pipe(
          map((res) => {
            return res;
          })
        );
      })
    );
  }

  public get freshQuickNavInfoResponse(): Observable<IQuickNavInfoResponse> {
    const curDate = new Date();
    if (!this.quickNavInfoResponse || !this.lastFetchedDate || (curDate.getTime() - this.lastFetchedDate.getTime() > this.MAX_AGE_MILLISECONDS)) {
      console.log(`Fetching new quick nav response`);
      return this.getSearchInfo().pipe(
        tap((res) => {
          this.quickNavInfoResponse = res;
          this.lastFetchedDate = curDate;
        })
      );
    }
    console.log(`Using cached quick nav response`);
    return of(this.quickNavInfoResponse);
  }

  private EMPTY_MODEL: QuickSearchResultModel = {
    count: 0,
    items: [],
    searchCoverage: 0,
  };

  public performClientSideSearch(
    eSearchType: eSearchType,
    searchText: string,
    itemsLimit: number = 1000,
    fullQueryType: boolean,
    filterByCategory?: string): Observable<QuickSearchResultModel> {

    return this.freshQuickNavInfoResponse.pipe(
      take(1),
      switchMap((res) => {
        const searchTypeAndInfo = res.SearchInfoList.find(info => info.SearchType == eSearchType);
        const clientSideSearchInfo = searchTypeAndInfo?.SearchInfo;
        if (!clientSideSearchInfo) {
          console.error(`Unable to find any client side search information for search type: ${eSearchType}`);
          return of(this.EMPTY_MODEL);
        }
        const requestPayload: IGetQuickSearchRequestPayload = {
          count: true,
          highlight: clientSideSearchInfo.HighlightFields,
          search: searchText,
          select: clientSideSearchInfo.SelectFields,
          top: itemsLimit,
          searchFields: clientSideSearchInfo.SearchFields,
          highlightPreTag: "<span class='quick-search-highlighted-text'>",
          highlightPostTag: "</span>",
          searchMode: "all",
          facets: clientSideSearchInfo.Facets,
          minimumCoverage: environment.minimumCoverage
        };
        if (fullQueryType) {
          requestPayload.queryType = "full";
        }
        // if( clientSideSearchInfo.ScoringProfile ){
        //     requestPayload.scoringProfile = clientSideSearchInfo.ScoringProfile;
        // }
        if (clientSideSearchInfo.OrderBy) {
          requestPayload.orderby = clientSideSearchInfo.OrderBy;
        }

        if (filterByCategory) {
          switch (filterByCategory) {
            case 'all':
              const resultTypeCondition = this.getResultTypeCondition([CategoryAPIName.crimdoc,
              CategoryAPIName.civildoc, CategoryAPIName.civilparty, CategoryAPIName.crimparty, CategoryAPIName.persondoc]);
              requestPayload.filter = `(${resultTypeCondition}) and ` + clientSideSearchInfo.Filter;
              break;
            default:
              requestPayload.filter = "(resulttype eq '" + filterByCategory + "') and " + clientSideSearchInfo.Filter;
              break;
          }
        } else {
          requestPayload.filter = clientSideSearchInfo.Filter;
        }

        const indexUrl = clientSideSearchInfo.SearchServiceUrl + "/indexes('" + clientSideSearchInfo.SearchIndexName + "')/docs/search.post.search?api-version=2020-06-30";

        try {
          const axiosResponseObs = from(axios.post(indexUrl, JSON.stringify(requestPayload), {
            headers: {
              "api-key": clientSideSearchInfo.SearchApiKey ?? "",
              "Content-Type": "application/json"
            }
          }));

          return axiosResponseObs.pipe(
            map((axiosResponse) => {
              const response: IAzureIndexResponseModel = axiosResponse.data;
              const result: QuickSearchResultModel = {
                count: response['@odata.count'],
                facets: response['@search.facets'],
                searchCoverage: response['@search.coverage'],
                items: response.value.map((item: IQuickSearchResponseItem) => item),
                mappingList: res.MappingList
              };
              return (result);
            })
          );

        } catch (error) {
          console.log(error);
          return of(this.EMPTY_MODEL);
          //return from(Promise.reject(console.error(error)));
        }
      })
    );
  }

  private getResultTypeCondition(excludedCategories: string[] | null): string {
    const apiCategoryNames = Object.values(CategoryAPIName);
    const excludedCategoryTypes = this.addPartyTypes(excludedCategories);

    return apiCategoryNames
      .filter(
        (categoryName) => !excludedCategoryTypes.includes(categoryName)
      )
      .map((key) => `(resulttype eq '${key}')`)
      .join(' or ');
  }

  private addPartyTypes(categoryNames: string[] | null): string[] {
    if (!categoryNames) {
      return [];
    }

    const extendedArray: string[] = [];
    categoryNames?.map(categoryName => {
      switch (categoryName) {
        case CategoryAPIName.civilcase:
          extendedArray.push(categoryName, CategoryAPIName.civilparty)
          break;
        case CategoryAPIName.crimcase:
          extendedArray.push(categoryName, CategoryAPIName.crimparty)
          break;
        default:
          extendedArray.push(categoryName)
          break;
      }
    })
    return extendedArray;
  }

  public getRecentLocations() {
    return this.auth.currentSession$.pipe(
      take(1),
      switchMap((sessionResponse) => {
        return this.http.post<any>(`${this.basePath}/RecentLocations`, { userId: sessionResponse?.UserId, top: 50 }).pipe(
          map((res) => {
            return res;
          })
        );
      })
    );
  }
}

